/**
 * Export builder - creates Drumly v1 export format
 */

import type { DrumlyExportV1, ExportBuilderArgs } from "./types";
import { LANE_NAMES, GM_TO_LANE } from "./constants";
import { tickToMsFast } from "./tempoUtils";
import { getTimeSignatureFromTrack, getBpmFromTrack } from "./parser";

/**
 * Generate lanes metadata
 */
function lanesMeta(): DrumlyExportV1["lanes"] {
  const perLane: number[][] = Array.from({ length: 8 }, () => []);
  for (const [k, v] of Object.entries(GM_TO_LANE)) {
    perLane[v].push(Number(k));
  }
  for (const arr of perLane) {
    arr.sort((a, b) => a - b);
  }
  return Array.from({ length: 8 }).map((_, lane) => ({
    lane,
    name: LANE_NAMES[lane as 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7],
    midi_notes: perLane[lane],
  }));
}

/**
 * Build Drumly v1 export format
 */
export function buildDrumlyExportV1(args: ExportBuilderArgs): DrumlyExportV1 {
  const {
    score,
    tempoIndex,
    syncMs,
    youtubeUrl,
    videoId,
    startSec,
    trackObj,
    ytTitle,
    ytArtist,
    ytDurationSec,
    lookaheadMs,
    hitMs,
    calibrateSpeed,
  } = args;

  const ts = getTimeSignatureFromTrack(trackObj);
  const bpm = getBpmFromTrack(trackObj, score, 120);

  // Convert events to ms list
  const raw = (score.events ?? []).map((e) => ({
    ms: Math.max(0, Math.round(tickToMsFast(e.t0, tempoIndex, score.ppq))),
    m: e.m | 0,
  }));

  // Merge extremely close ms to avoid same-time split after rounding
  const exportMergeMs = 1;
  raw.sort((a, b) => a.ms - b.ms);

  const merged: Array<{ ms: number; m: number }> = [];
  for (const r of raw) {
    const last = merged.length ? merged[merged.length - 1] : null;
    if (!last) {
      merged.push({ ...r });
    } else if (r.ms <= last.ms + exportMergeMs) {
      last.m |= r.m;
    } else {
      merged.push({ ...r });
    }
  }

  const dt: number[] = [];
  const m: number[] = [];
  let prev = 0;
  for (let i = 0; i < merged.length; i++) {
    const cur = merged[i].ms;
    dt.push(i === 0 ? cur : Math.max(0, cur - prev));
    prev = cur;
    m.push(merged[i].m | 0);
  }

  return {
    v: 1,
    title: ytTitle || trackObj?.title || "Unknown Title",
    artist: ytArtist || trackObj?.artist || "Unknown Artist",
    bpm,
    ts,
    duration_ms: Math.max(0, Math.round((Number(ytDurationSec ?? 0) || 0) * 1000)),
    source: {
      type: "youtube",
      url: youtubeUrl,
      video_id: videoId,
      start_sec: startSec || undefined,
    },
    sync_ms: Math.round(syncMs),
    lookahead_ms: Math.round(lookaheadMs),
    hit_ms: Math.round(hitMs),
    ...(calibrateSpeed != null ? { calibrate_speed: calibrateSpeed } : {}),
    lanes: lanesMeta(),
    dt,
    m,
    chart: {
      format: "score_v2",
      ppq: score.ppq,
      tempo_map: score.tempo_map ?? [],
      events: score.events ?? [],
    },
  };
}

/**
 * Download JSON file
 */
export function downloadJson(filename: string, obj: any): void {
  const blob = new Blob([JSON.stringify(obj, null, 2)], {
    type: "application/json;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
